import React from "react";
import {Button, Col, Row} from "reactstrap";
import {inject, observer} from "mobx-react";
import configuration from "../../../../configuration/configuration";
import ModalAkitatek from "../../../../components/Akitatek/Modal/Modal";
import WidgetSearch from "../../../../components/Akitatek/Form/Search/WidgetSearch";
import PickAsset from "../../Asset/PickAsset";
import CarteRepliable from "./CarteRepliable";
import ReglagesFond from "./ReglagesFond";
import ClientModeComponent, {ECRAN} from "../../../screen/ClientModeComponent";
import {aEnregistrer, resoudre, valeursParDefaut} from "../../../screen/fonds/reglages";
import {ID_IMAGE_FOND, ID_IMAGE_GANT, ID_REGLAGES, REGLAGES_SAISIE, premiereImage} from "../../../screen/saisie/reglagesSaisie";

/** Fiche d'exemple montrée dans l'aperçu : le cas de tous les jours en boutique, le curseur sur le téléphone. */
const EXEMPLE = {nom: "Dupont-Martin", prenom: "Léa", telephone: "0612345678"};

/** Échelle de l'aperçu : l'écran de la boutique (1 080 px de large) réduit à environ 300 px. */
const ECHELLE = 0.28;

/**
 * Carte « Réglage saisi client » : habillage de l'écran client en mode Client (la fiche que la Caisse saisit).
 * Style, image de fond et gant choisis dans la galerie, assombrissement, taille du curseur, message du bas. Tout se règle avec un
 * aperçu en direct ; rien n'est appliqué à l'écran de la boutique avant « Enregistrer » (les images, elles, le sont tout de suite,
 * comme celles des fonds du bandeau).
 */
const ReglageSaisieClient = (inject("rootStore")(observer(class ReglageSaisieClient extends React.Component {

    state = {
        brouillon: null,   // réglages en cours de modification (montrés dans l'aperçu, pas encore enregistrés)
        galerie: null,     // identifiant de la liste d'images pour laquelle la galerie est ouverte
    };

    get auth() {
        return this.props.rootStore.authStore;
    }

    get notifications() {
        return this.props.rootStore.notificationStore;
    }

    reglagesActuels() {
        return this.state.brouillon || this.auth.fondReglages[ID_REGLAGES];
    }

    changer(cle, valeur) {
        const base = resoudre(REGLAGES_SAISIE, this.reglagesActuels());
        this.setState({brouillon: {...base, [cle]: valeur}});
    }

    async enregistrer() {
        try {
            await this.auth.enregistrerReglagesFond(ID_REGLAGES, aEnregistrer(REGLAGES_SAISIE, this.state.brouillon));
            this.setState({brouillon: null});
            this.notifications.setNotification("Réglage de la saisie client enregistré", "success");
        } catch (erreur) {
            this.notifications.setNotification(typeof erreur === "string" ? erreur : "Réglage non enregistré", "error");
        }
    }

    ouvrirGalerie(id) {
        this.props.rootStore.imageStore.pickAsset = null;
        this.setState({galerie: id});
    }

    fermerGalerie = () => this.setState({galerie: null});

    /** Une image a été choisie dans la galerie : elle REMPLACE l'image précédente (une seule image de fond, un seul gant). */
    imageChoisie = async () => {
        const imageStore = this.props.rootStore.imageStore;
        const asset = imageStore.pickAsset;
        const id = this.state.galerie;
        imageStore.pickAsset = null;
        this.setState({galerie: null});
        if (!asset || !asset.id || !id) return;

        try {
            for (const ancienne of this.auth.fondImages[id] || []) await this.auth.retirerImageFond(ancienne.id);
            await this.auth.ajouterImageFond(id, asset.id);
            this.notifications.setNotification("Image enregistrée", "success");
        } catch (erreur) {
            this.notifications.setNotification(typeof erreur === "string" ? erreur : "Image non enregistrée", "error");
        }
    };

    async retirer(id) {
        try {
            for (const image of this.auth.fondImages[id] || []) await this.auth.retirerImageFond(image.id);
            this.notifications.setNotification("Image retirée : retour à l'habillage d'origine", "success");
        } catch (erreur) {
            this.notifications.setNotification("Image non retirée", "error");
        }
    }

    /** Une image (ou rien) de la galerie : miniature, retrait, et choix dans la galerie (téléversement compris). */
    choixImage(id, titre, aide, vide) {
        const image = premiereImage(this.auth.fondImages[id]);
        return (
            <div className="mb-3">
                <strong className="d-block">{titre}</strong>
                <small className="text-muted d-block mb-2">{aide}</small>
                <div className="d-flex align-items-center flex-wrap">
                    {image ? (
                        <div style={{position: "relative", marginRight: 12}}>
                            <img src={configuration.API + image.url} alt={image.nom} title={image.nom} height={56}
                                 style={{background: "repeating-conic-gradient(#ddd 0% 25%, #fff 0% 50%) 50% / 12px 12px", borderRadius: 4, maxWidth: 140, objectFit: "contain"}}/>
                            <button type="button" className="btn btn-danger btn-sm p-0" title="Retirer cette image"
                                    style={{position: "absolute", top: -6, right: -6, width: 20, height: 20, lineHeight: "18px", borderRadius: "50%"}}
                                    onClick={() => this.retirer(id)}>×</button>
                        </div>
                    ) : <span className="text-muted mr-3">{vide}</span>}
                    <Button size="sm" color="secondary" onClick={() => this.ouvrirGalerie(id)}>
                        {image ? "Changer l'image" : "Choisir dans la galerie"}
                    </Button>
                </div>
            </div>
        );
    }

    render() {
        const reglagesEnCours = this.reglagesActuels();
        const fond = premiereImage(this.auth.fondImages[ID_IMAGE_FOND]);
        const gant = premiereImage(this.auth.fondImages[ID_IMAGE_GANT]);
        const url = image => (image ? configuration.API + image.url : null);

        return (
            <CarteRepliable id="reglage-saisie-client" titre="Réglage saisi client" className="mb-4">
                <>
                    <p className="text-muted">
                        Habillage de l'écran client quand il montre la fiche que vous saisissez à la Caisse : le client vérifie qu'il n'y a pas de faute.
                    </p>
                    <Row>
                        <Col md="5" lg="4" className="mb-3">
                            <small className="text-muted d-block mb-1">Aperçu (nom, prénom et téléphone, le curseur sur le téléphone)</small>
                            <div style={{width: ECRAN.largeur * ECHELLE, height: ECRAN.hauteur * ECHELLE, overflow: "hidden", borderRadius: ".375rem", boxShadow: "0 2px 10px rgba(0,0,0,.35)"}}>
                                <div style={{width: ECRAN.largeur, transform: `scale(${ECHELLE})`, transformOrigin: "top left"}}>
                                    <ClientModeComponent apercu datas={EXEMPLE} curseurSur="telephone"
                                                         reglagesSaisie={reglagesEnCours}
                                                         imageFond={url(fond)} imageGant={url(gant)}/>
                                </div>
                            </div>
                        </Col>
                        <Col md="7" lg="8">
                            {this.choixImage(ID_IMAGE_FOND, "Image de fond", "Remplace le fond sombre. Appliquée tout de suite à l'écran client.", "Fond d'origine")}
                            {this.choixImage(ID_IMAGE_GANT, "Gant de sélection", "Remplace la main qui pointe le champ en cours de saisie (une image avec fond transparent, de préférence).", "Gant d'origine")}
                            <ReglagesFond titre="Style et réglages" ouvert schema={REGLAGES_SAISIE}
                                          valeurs={reglagesEnCours} enregistrees={this.auth.fondReglages[ID_REGLAGES]}
                                          onChange={(cle, valeur) => this.changer(cle, valeur)}
                                          onEnregistrer={() => this.enregistrer()}
                                          onOrigine={() => this.setState({brouillon: valeursParDefaut(REGLAGES_SAISIE)})}/>
                        </Col>
                    </Row>

                    <ModalAkitatek show={!!this.state.galerie}
                                   title={<WidgetSearch icon={"fa fa-search bg-primary p-1 font-10xl mr-3 float-left"} col={"col-12"} category={"images"}/>}
                                   children={<PickAsset onCallback={this.imageChoisie}/>}
                                   onCallback={this.fermerGalerie}/>
                </>
            </CarteRepliable>
        );
    }
})));

export default ReglageSaisieClient;
